import { NoddiAsyncError } from 'noddi-types';
import { getFirstErrorCode } from './error';

/**
 * Cap per serialized body. Sentry truncates event payloads server-side anyway,
 * and one runaway list response should not push the rest of the context out.
 */
const MAX_BODY_CHARS = 8_000;

const stringify = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    // Circular reference or a non-serializable value (File, class instance…).
    return String(value);
  }
};

const truncate = (text: string) =>
  text.length > MAX_BODY_CHARS ? `${text.slice(0, MAX_BODY_CHARS)}…[truncated]` : text;

/**
 * Renders a request or response body as something readable in Sentry. Binary
 * payloads (PDF/xlsx downloads) are described rather than dumped.
 */
const serializeBody = (body: unknown): string | undefined => {
  if (body === undefined || body === null || body === '') {
    return undefined;
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return `[Blob ${body.type || 'unknown type'}, ${body.size} bytes]`;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    // React Native FormData exposes getParts(), not the browser's keys(). Keep file contents opaque.
    return typeof body.keys === 'function' ? `[FormData: ${Array.from(body.keys()).join(', ')}]` : '[FormData]';
  }
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
    return `[ArrayBuffer, ${body.byteLength} bytes]`;
  }
  return truncate(typeof body === 'string' ? body : stringify(body));
};

/**
 * The Sentry `extra` for a failed noddiAsync request: url, method, status and —
 * the part that makes an issue actionable without a reproduction — the query
 * params, the body we sent and the body the backend answered with.
 *
 * Assumes the caller ships these to Sentry only, never to a third party, since
 * request and response bodies can hold customer data.
 */
export const getNetworkErrorExtra = (error: unknown) => {
  const asyncError = error as NoddiAsyncError;

  return {
    requestUrl: asyncError.config?.url,
    requestMethod: asyncError.config?.method,
    queryParams: asyncError.config?.params,
    requestBody: serializeBody(asyncError.config?.data),
    responseBody: serializeBody(asyncError.response?.data),
    errorCode: asyncError.status ?? asyncError.response?.status,
    apiErrorCode: getFirstErrorCode(asyncError)
  };
};
